/**
 * Screen Preview Configuration Constants
 *
 * Centralized configuration for screen preview rendering.
 * Single source of truth for visual settings across:
 * - TemplatePanel (template palette)
 * - FlowScreenNode (canvas nodes)
 * - ScreenPreviewRenderer (preview rendering service)
 * - OnboardingScreenEditorDialog (editor preview)
 *
 * Sprint: sprint-2
 * Task: T002.0
 */

/**
 * Screen Design Size - Single Source of Truth
 *
 * Based on iPhone 14 Pro viewport dimensions (430×932).
 * All screen content is designed at this resolution.
 * Use AutoScaleScreenContainer to responsively scale to any container size.
 */
export const SCREEN_DESIGN_SIZE = {
  /** Base design width (iPhone 14 Pro) */
  WIDTH: 430,
  /** Base design height (iPhone 14 Pro) */
  HEIGHT: 932,
  /** Computed aspect ratio (~0.461) */
  get ASPECT_RATIO() {
    return this.WIDTH / this.HEIGHT;
  }
} as const;

/**
 * Preview scale factors for different contexts
 *
 * TEMPLATE_PANEL: Small preview for template palette (left sidebar)
 * CANVAS_NODE: Medium preview for flow canvas nodes
 * FULL_SIZE: Full size for detail view or editing
 */
export const SCREEN_PREVIEW_SCALE = {
  TEMPLATE_PANEL: 0.3,
  CANVAS_NODE: 0.5,
  FULL_SIZE: 1.0,
} as const;

/**
 * Screen dimensions for canvas rendering
 *
 * Unified with SCREEN_DESIGN_SIZE (430×932) for consistency.
 */
export const SCREEN_DIMENSIONS = {
  WIDTH: 430,  // px - unified with SCREEN_DESIGN_SIZE
  HEIGHT: 932, // px - unified with SCREEN_DESIGN_SIZE
  get ASPECT_RATIO() {
    return this.WIDTH / this.HEIGHT;
  }
} as const;

/**
 * Canvas node dimensions
 *
 * Single source of truth for node size on canvas.
 * Unified with SCREEN_DESIGN_SIZE (430×932) for consistency.
 * Used by FlowScreenNode and TemplatePanel.
 */
export const CANVAS_NODE_DIMENSIONS = {
  WIDTH: 430,  // px - unified with SCREEN_DESIGN_SIZE
  HEIGHT: 932, // px - unified with SCREEN_DESIGN_SIZE
  get ASPECT_RATIO() {
    return this.WIDTH / this.HEIGHT;
  }
} as const;

/**
 * Template panel preview configuration
 *
 * 🔧 ADJUSTABLE CONSTANTS for fine-tuning template preview appearance:
 *
 * CONTAINER_SCALE: Controls the size of template container relative to canvas node
 *   - Adjust this to change overall preview size while maintaining aspect ratio
 *   - Recommended range: 0.3 - 0.7
 *   - Current: 0.5 → container 215×466px
 *
 * PREVIEW_SCALE: Controls how preview content fills the container
 *   - 1.0 = preview fills entire container (no padding)
 *   - 0.9 = preview with small margin
 *   - 1.05 = preview slightly overflows for zoom effect
 *   - Recommended range: 0.8 - 1.1
 *
 * Formula:
 *   Container Width = CANVAS_NODE_DIMENSIONS.WIDTH × CONTAINER_SCALE
 *   Container Height = CANVAS_NODE_DIMENSIONS.HEIGHT × CONTAINER_SCALE
 */
export const TEMPLATE_PREVIEW_CONFIG = {
  /** 🔧 Template container size relative to canvas node (0.3 - 0.7) */
  CONTAINER_SCALE: 0.5,

  /** 🔧 How preview fills container (0.8 - 1.1) */
  PREVIEW_SCALE: 1,

  /** Computed container width (read-only) */
  get CONTAINER_WIDTH() {
    return Math.round(CANVAS_NODE_DIMENSIONS.WIDTH * this.CONTAINER_SCALE);
  },

  /** Computed container height (read-only) */
  get CONTAINER_HEIGHT() {
    return Math.round(CANVAS_NODE_DIMENSIONS.HEIGHT * this.CONTAINER_SCALE);
  },
} as const;

/**
 * Template panel UI settings
 *
 * Controls behavior and layout of the template palette
 */
export const TEMPLATE_PANEL_CONFIG = {
  /** Debounce delay for search input (ms) */
  SEARCH_DEBOUNCE_MS: 300,

  /** Maximum number of templates to show at once */
  MAX_VISIBLE_TEMPLATES: 50,

  /** Spacing between template items (px) */
  ITEM_SPACING: 16,

  /** Panel width (px) */
  PANEL_WIDTH: 280,

  /**
   * Template item height including spacing (px)
   * Auto-calculated based on container height + space for name/spacing
   */
  get ITEM_HEIGHT() {
    return TEMPLATE_PREVIEW_CONFIG.CONTAINER_HEIGHT + 60; // 60px for name + spacing
  },
} as const;

/**
 * Screen type colors for visual distinction
 *
 * Maps canvas screen type to Material-UI theme colors.
 * Used in FlowScreenNode and ScreenPreviewRenderer.
 */
export const SCREEN_TYPE_COLORS = {
  form: '#4caf50',      // Green - Input/data collection
  display: '#2196f3',   // Blue - Information display
  decision: '#ff9800',  // Orange - User choice/branching
  action: '#f44336',    // Red - Action/process
} as const;

/**
 * Screen type icons for visual distinction
 *
 * Emoji icons representing each screen type.
 * Used in FlowScreenNode headers.
 */
export const SCREEN_TYPE_ICONS = {
  form: '📝',
  display: '📄',
  decision: '🔀',
  action: '⚡',
} as const;

/**
 * Default placeholder image URLs for mock screens
 *
 * Used by mockTemplateScreens when creating template examples.
 * Uses placehold.co for reliable working images (via.placeholder.com has DNS issues).
 */
export const PLACEHOLDER_IMAGES = {
  /** Main screen image (375x400 purple gradient) */
  MAIN: 'https://placehold.co/375x400/E8DEF8/1D192B?text=Image',
  /** Slide/list item images (100x100) */
  SLIDE_1: 'https://placehold.co/100x100/E8DEF8/1D192B?text=1',
  SLIDE_2: 'https://placehold.co/100x100/D0BCFF/1D192B?text=2',
  SLIDE_3: 'https://placehold.co/100x100/CAC4D0/1D192B?text=3',
  /** Icon-sized placeholder (48x48) */
  ICON: 'https://placehold.co/48x48/E8DEF8/1D192B?text=Icon',
} as const;

// Type exports for TypeScript inference
export type ScreenDesignSize = typeof SCREEN_DESIGN_SIZE;
export type ScreenPreviewScale = typeof SCREEN_PREVIEW_SCALE[keyof typeof SCREEN_PREVIEW_SCALE];
export type ScreenTypeColor = typeof SCREEN_TYPE_COLORS[keyof typeof SCREEN_TYPE_COLORS];
export type ScreenTypeIcon = typeof SCREEN_TYPE_ICONS[keyof typeof SCREEN_TYPE_ICONS];
